import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, useOutletContext } from "react-router-dom";
import { API_URL } from "../../constant";
import {
  MdOutlineAccountBalanceWallet,
  MdPayment,
  MdCheckCircle,
  MdErrorOutline,
  MdRefresh,
  MdClose,
  MdInfoOutline,
  MdHistory,
} from "react-icons/md";
import { FaCoins, FaCcVisa, FaCcMastercard, FaCcAmex, FaStripe } from "react-icons/fa";
import "./RetailTopCoinPurchase.css";

const PRESET_QUANTITIES = [500, 1000, 2500, 5000, 10000, 25000];

/**
 * Formats a cash value (e.g. cost per TopCoin) with appropriate precision.
 * Standard amounts like £0.25 show 2 decimal places (£0.25).
 * Fractional penny values like £0.005 show up to 4 decimal places (£0.005) without rounding to £0.01.
 */
export const formatCashValue = (val) => {
  const num = Number(val);
  if (isNaN(num)) return "0.00";
  return num.toLocaleString("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
};

export default function RetailTopCoinPurchase() {
  const navigate = useNavigate();
  const location = useLocation();
  const outletContext = useOutletContext();
  const userData = outletContext?.userData;

  // Rate config state (loaded dynamically from UserTopCoin table, default 0.005)
  const [costPerTopCoin, setCostPerTopCoin] = useState(0.005);
  const [vatRate, setVatRate] = useState(0.20);
  const [currentBalance, setCurrentBalance] = useState(0);

  // Form input state
  const [quantity, setQuantity] = useState("1000");
  const [inputError, setInputError] = useState("");

  // Stripe checkout & processing state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);

  // Successful purchase modal state
  const [successModalData, setSuccessModalData] = useState(null);

  // Avoid double verification for same session_id in strict mode
  const processedSessionRef = useRef(null);

  // 1. Fetch live rate from IT portal config
  const fetchRateConfig = async () => {
    const token = localStorage.getItem("auth_token");
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/retail-business/topcoin-purchase/rate`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.cost_per_topcoin) {
          setCostPerTopCoin(parseFloat(data.cost_per_topcoin));
        }
        if (data.vat_rate) {
          setVatRate(parseFloat(data.vat_rate));
        }
        if (data.current_balance !== undefined) {
          setCurrentBalance(data.current_balance);
        }
      }
    } catch (err) {
      console.error("Failed to load TopCoin purchase rate:", err);
    }
  };

  // 2. Verify Stripe Checkout return callback
  useEffect(() => {
    const token = localStorage.getItem("auth_token");
    if (!token) {
      navigate("/retail-agent-login");
      return;
    }

    fetchRateConfig();

    const queryParams = new URLSearchParams(location.search);
    const sessionId = queryParams.get("session_id");
    const canceled = queryParams.get("canceled");

    if (canceled) {
      setStatusMessage({
        type: "warning",
        text: "TopCoin checkout was cancelled. No charges were made to your card.",
      });
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (sessionId && processedSessionRef.current !== sessionId) {
      processedSessionRef.current = sessionId;
      setIsVerifying(true);

      fetch(`${API_URL}/api/retail-business/topcoin-purchase/success`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ session_id: sessionId }),
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.status === "success") {
            setSuccessModalData(data.data);
            if (data.current_balance !== undefined) {
              setCurrentBalance(data.current_balance);
            }
          } else {
            setStatusMessage({
              type: "error",
              text: data.message || "Payment status could not be verified.",
            });
          }
        })
        .catch((err) => {
          console.error("Error confirming payment:", err);
          setStatusMessage({
            type: "error",
            text: "Network error confirming payment. Please check your transaction history.",
          });
        })
        .finally(() => {
          setIsVerifying(false);
          window.history.replaceState({}, document.title, window.location.pathname);
        });
    }
  }, [location.search, navigate]);

  // Handle QTY input with numeric validation
  const handleQuantityChange = (val) => {
    const cleanVal = val.replace(/\D/g, "");
    setQuantity(cleanVal);

    if (!cleanVal) {
      setInputError("Please enter a TopCoin quantity.");
    } else if (parseInt(cleanVal, 10) <= 0) {
      setInputError("Quantity must be greater than 0.");
    } else {
      setInputError("");
    }
  };

  const handleSelectPreset = (qty) => {
    setQuantity(qty.toString());
    setInputError("");
  };

  // Calculations
  const numericQty = parseInt(quantity, 10) || 0;
  const totalValue = +(numericQty * costPerTopCoin).toFixed(2);
  const vatAmount = +(totalValue * vatRate).toFixed(2);
  const totalPayable = +(totalValue + vatAmount).toFixed(2);

  // Initiate Stripe Checkout
  const handleProceedToCheckout = async (e) => {
    e.preventDefault();

    if (!numericQty || numericQty <= 0) {
      setInputError("Please enter a valid purchase quantity (minimum 1).");
      return;
    }

    const token = localStorage.getItem("auth_token");
    if (!token) {
      navigate("/retail-agent-login");
      return;
    }

    setIsSubmitting(true);
    setStatusMessage(null);

    try {
      const currentUrl = window.location.origin + window.location.pathname;

      const response = await fetch(`${API_URL}/api/retail-business/topcoin-purchase/checkout`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          quantity: numericQty,
          success_url: currentUrl,
          cancel_url: `${currentUrl}?canceled=true`,
        }),
      });

      const data = await response.json();

      if (response.ok && data.url) {
        window.location.href = data.url;
      } else {
        setStatusMessage({
          type: "error",
          text: data.message || data.error || "Failed to start checkout. Please try again.",
        });
        setIsSubmitting(false);
      }
    } catch (err) {
      console.error("Checkout error:", err);
      setStatusMessage({
        type: "error",
        text: "Error connecting to checkout gateway. Please check your internet connection.",
      });
      setIsSubmitting(false);
    }
  };

  // Date and Time matching ManualTopCoinWindow & RetailTcDistribution
  const now = new Date();
  const displayDate = now.toLocaleDateString("en-GB");
  const displayTime = now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

  const shopName =
    userData?.retail_name ||
    userData?.shop_details?.retail_name ||
    `${userData?.first_name || "Retail"} ${userData?.last_name || "Business"}`.trim();

  return (
    <div className="tcp-outer">
      {/* Dark Plum Decorative Border Frame matching ManualTopCoinWindow & RetailTcDistribution */}
      <div className="tcp-border-frame">
        {/* White Receipt Card */}
        <div className="tcp-card">
          {/* Header */}
          <div className="tcp-header">
            <div className="tcp-header-left">
              <h3>
                TopCoin UK<br />(TCUK)
              </h3>
            </div>
            <div className="tcp-header-right">
              {displayDate} {displayTime}
            </div>
          </div>

          {/* Branding Row */}
          <div className="tcp-branding">
            <p className="brand-name">TopCoinLand</p>
            <a
              className="brand-link"
              href="https://www.topcoinland.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              www.topcoinland.com
            </a>
            <div className="tcp-page-badge">
              <FaCoins /> TopCoin Purchase for Retail Business
            </div>
          </div>

          {/* Account & Available Balance Bar */}
          <div className="tcp-account-bar">
            <div>
              <span style={{ opacity: 0.75 }}>Retail Account: </span>
              <span className="store-name">{shopName}</span>
            </div>
            <div className="tcp-account-bar-actions">
              <div>
                <span style={{ opacity: 0.75 }}>Available Balance: </span>
                <span className="wallet-balance">
                  {Number(currentBalance).toLocaleString()} TC
                </span>
              </div>
              {/* <button
                type="button"
                className="tcp-history-link-btn"
                onClick={() => navigate("/retail-business/topcoin-purchase/history")}
                title="View your TopCoin purchase history"
              >
                <MdHistory size={16} />
                <span>Purchase History</span>
              </button> */}
            </div>
          </div>

          {/* Verifying Notification Banner */}
          {isVerifying && (
            <div className="tcp-alert tcp-alert-info">
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span className="tcp-spinner-sm" style={{ borderTopColor: "#166534" }} />
                <span>Verifying your Stripe payment and crediting TopCoins...</span>
              </div>
            </div>
          )}

          {/* Status Message Banner */}
          {statusMessage && (
            <div className={`tcp-alert tcp-alert-${statusMessage.type}`}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                {statusMessage.type === "success" && <MdCheckCircle size={18} />}
                {statusMessage.type === "error" && <MdErrorOutline size={18} />}
                {statusMessage.type === "warning" && <MdInfoOutline size={18} />}
                <span>{statusMessage.text}</span>
              </div>
              <button
                className="tcp-alert-close"
                onClick={() => setStatusMessage(null)}
                aria-label="Close"
              >
                <MdClose />
              </button>
            </div>
          )}

          {/* Purchase Form */}
          <form onSubmit={handleProceedToCheckout}>
            {/* Enter TopCoin Purchase QTY */}
            <div className="tcp-form-row mt-4 mb-5">
              <label htmlFor="tcpQuantity" className="label-text">
                Enter TopCoin Purchase QTY:
              </label>
              <input
                id="tcpQuantity"
                name="quantity"
                className={`field-input ${inputError ? "is-invalid" : ""}`}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="e.g. 1000"
                value={quantity}
                onChange={(e) => handleQuantityChange(e.target.value)}
                disabled={isSubmitting}
                autoComplete="off"
                required
              />
            </div>
            {inputError && <div className="tcp-field-error">{inputError}</div>}

            {/* Quick Presets Chips */}
            {/* <div className="tcp-presets-row">
              {PRESET_QUANTITIES.map((qty) => (
                <button
                  key={qty}
                  type="button"
                  className={`tcp-preset-btn ${numericQty === qty ? "active" : ""}`}
                  onClick={() => handleSelectPreset(qty)}
                  disabled={isSubmitting}
                >
                  {qty.toLocaleString()} TC
                </button>
              ))}
            </div> */}

            {/* Cost per TopCoin (Config Value via IT Portal) */}
            <div className="tcp-calc-row">
              <span className="label-text">Cost per TopCoin:</span>
              <span className="calc-value">£{formatCashValue(costPerTopCoin)} / TC</span>
            </div>

            {/* Total Value */}
            <div className="tcp-calc-row">
              <span className="label-text">Total Value:</span>
              <span className="calc-value">£{totalValue.toFixed(2)}</span>
            </div>

            {/* VAT (20%) */}
            <div className="tcp-calc-row">
              <span className="label-text">VAT ({Math.round(vatRate * 100)}%):</span>
              <span className="calc-value">£{vatAmount.toFixed(2)}</span>
            </div>

            {/* Total Payable including VAT */}
            <div className="tcp-total-row">
              <span className="label-text">Total Payable including VAT:</span>
              <span className="total-value">£{totalPayable.toFixed(2)}</span>
            </div>

            {/* Standard Pricing Formula Explainer Box */}
            {/* <div className="tcp-formula-box">
              <div className="tcp-formula-title">Standard Pricing Formula</div>
              <div className="tcp-formula-item">
                <span>Example:</span>
                <span>1,000 TopCoins @ £{costPerTopCoin.toFixed(2)}</span>
              </div>
              <div className="tcp-formula-item">
                <span>Total Value:</span>
                <span>1000 * {costPerTopCoin.toFixed(2)} = <strong>£{(1000 * costPerTopCoin).toFixed(2)}</strong></span>
              </div>
              <div className="tcp-formula-item">
                <span>Total Payable including VAT:</span>
                <span>{(1000 * costPerTopCoin).toFixed(2)} * 1.20 = <strong>£{(1000 * costPerTopCoin * 1.20).toFixed(2)}</strong></span>
              </div>
            </div> */}

            {/* Submit / Checkout Area */}
            <div className="tcp-submit-area">
              <button
                type="submit"
                className="tcp-submit-btn"
                disabled={isSubmitting || !!inputError || numericQty <= 0}
              >
                <MdPayment size={18} />
                {isSubmitting ? (
                  <>
                    <span className="tcp-spinner-sm" />
                    <span>CONNECTING TO STRIPE...</span>
                  </>
                ) : (
                  <span>PROCEED TO CHECKOUT</span>
                )}
              </button>

              <div className="tcp-stripe-badge">
                <FaStripe size={32} style={{ color: "#635bff" }} />
                <span>Payable securely via Credit / Debit Card</span>
                <div className="tcp-card-icons">
                  <FaCcVisa title="Visa" />
                  <FaCcMastercard title="Mastercard" />
                  <FaCcAmex title="American Express" />
                </div>
              </div>

              <div style={{ marginTop: "12px", display: "flex", gap: "10px", justifyContent: "center", flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="tcp-refresh-btn"
                  onClick={() => navigate("/retail-business/topcoin-purchase/history")}
                >
                  <MdHistory size={16} />
                  <span>View TopCoin Purchase History</span>
                </button>
                <button
                  type="button"
                  className="tcp-refresh-btn"
                  onClick={() => navigate("/retail-business/wallet")}
                >
                  <MdOutlineAccountBalanceWallet size={16} />
                  <span>Return to Retail Wallets</span>
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Payment Success Receipt Modal */}
      {successModalData && (
        <div className="tcp-modal-overlay" onClick={() => setSuccessModalData(null)}>
          <div className="tcp-modal-box" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="tcp-modal-close-icon"
              onClick={() => setSuccessModalData(null)}
              aria-label="Close"
              title="Close"
            >
              <MdClose size={18} />
            </button>
            <div className="tcp-modal-icon-circle">
              <MdCheckCircle />
            </div>
            <h3 className="tcp-modal-title">Payment Completed!</h3>
            <p className="tcp-modal-subtitle">
              Your TopCoins have been credited to your retail wallet.
            </p>

            <div className="tcp-receipt-summary">
              <div className="tcp-receipt-row">
                <span>TopCoins Credited:</span>
                <strong style={{ color: "#701a75" }}>
                  +{Number(successModalData.quantity).toLocaleString()} TC
                </strong>
              </div>
              <div className="tcp-receipt-row">
                <span>Unit Cost:</span>
                <span>£{formatCashValue(successModalData.unit_cost)}</span>
              </div>
              <div className="tcp-receipt-row">
                <span>Total Value (excl. VAT):</span>
                <span>£{Number(successModalData.total_value || 0).toFixed(2)}</span>
              </div>
              <div className="tcp-receipt-row">
                <span>VAT ({Math.round(successModalData.vat_rate !== undefined ? successModalData.vat_rate : vatRate * 100)}%):</span>
                <span>£{Number(successModalData.vat_amount || 0).toFixed(2)}</span>
              </div>
              <div className="tcp-receipt-row total-line">
                <span>Total Paid:</span>
                <span>£{Number(successModalData.total_payable || 0).toFixed(2)}</span>
              </div>
            </div>

            <div className="tcp-modal-btn-row">
              <button
                type="button"
                className="tcp-btn-gray"
                onClick={() => setSuccessModalData(null)}
              >
                Close
              </button>
              <button
                type="button"
                className="tcp-btn-purple"
                onClick={() => navigate("/retail-business/topcoin-purchase/history")}
              >
                <MdHistory size={15} />
                <span>Purchase History</span>
              </button>
              <button
                type="button"
                className="tcp-btn-purple"
                onClick={() => navigate("/retail-business/wallet")}
              >
                <MdOutlineAccountBalanceWallet size={15} />
                <span>Wallet</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
